import { PRICE_TYPES } from '../constants';

export const findAuctionEndDate = (inputString, listingDate) => {
    if (!inputString) return null;
    
    // Remove crossed-out text and square brackets
    inputString = inputString.replace(/\[-\][^]*?\[\/-\]/g, '');
    inputString = inputString.replace(/\[[^[\]]*?\]/g, '');
    
    // If listingDate is provided, ensure it's a Date object
    const listDate = listingDate instanceof Date ? listingDate : new Date();
    const listingYear = listDate.getFullYear();
    
    // Helper function to determine the correct year for a date
    const determineCorrectYear = (month, day) => {
        // Create a date with the listing year
        const dateWithListingYear = new Date(listingYear, month, day);
        
        // If this date is earlier than the listing date, it's likely for next year
        if (dateWithListingYear < listDate && 
            // Only adjust if the difference is significant (more than 7 days)
            (listDate - dateWithListingYear) > 7 * 24 * 60 * 60 * 1000) {
            return listingYear + 1;
        }
        
        return listingYear;
    };
    
    // Common date patterns in auction listings with extractors
    const datePatterns = [
        {
            // Pattern: "Auction ends on June 15, 2023" or "Auction ending on June 15, 2023"
            pattern: /(?:auction\s+end(?:s|ing)?(?:\s+on)?|end(?:s|ing)?(?:\s+on)?)\s+([A-Za-z]+\s+\d{1,2}(?:st|nd|rd|th)?,?\s+\d{4})/i,
            extract: (match) => {
                // Format: "June 15, 2023" - already has year
                return new Date(match[1].replace(/(?:st|nd|rd|th)/g, ''));
            }
        },
        {
            // Pattern: "Ending 6/15/2023" or "Ends 6/15/23" or "ends 5/11 at 6pm CST"
            pattern: /(?:end(?:s|ing)?(?:\s+on)?)\s+(\d{1,2}\/\d{1,2}(?:\/\d{2,4})?(?:\s+at\s+\d{1,2}(?::\d{2})?(?:am|pm|AM|PM)?(?:\s+[A-Za-z]{3,4})?)?)/i,
            extract: (match) => {
                // Format: "6/15/2023" or "6/15/23" or "5/11 at 6pm CST"
                // Extract just the date part
                const dateStr = match[1].split(/\s+at\s+/)[0];
                const dateParts = dateStr.split('/');
                
                if (dateParts.length === 3) {
                    const month = parseInt(dateParts[0], 10) - 1; // JS months are 0-indexed
                    const day = parseInt(dateParts[1], 10);
                    let year = parseInt(dateParts[2], 10);
                    // Handle 2-digit years
                    if (year < 100) {
                        year += year < 50 ? 2000 : 1900;
                    }
                    return new Date(year, month, day);
                } else if (dateParts.length === 2) {
                    // Handle MM/DD format without year
                    const month = parseInt(dateParts[0], 10) - 1;
                    const day = parseInt(dateParts[1], 10);
                    const year = determineCorrectYear(month, day);
                    return new Date(year, month, day);
                }
                return null;
            }
        },
        {
            // Pattern: "Auction closes 15 June 2023" or "Auction closing June 15" or "Closing bids on May 14"
            pattern: /(?:auction\s+clos(?:es|ing)|clos(?:es|ing)(?:\s+bids)?)\s+(?:on\s+)?(\d{1,2}\s+[A-Za-z]+\s+\d{2,4}|[A-Za-z]+\s+\d{1,2}(?:st|nd|rd|th)?,?\s+\d{0,4})/i,
            extract: (match) => {
                // Handle both "15 June 2023" and "June 15, 2023" formats
                const dateStr = match[1].replace(/(?:st|nd|rd|th)/g, '');
                
                // Check if it's "15 June 2023" format
                if (/^\d{1,2}\s+[A-Za-z]+/.test(dateStr)) {
                    const parts = dateStr.split(' ');
                    const day = parseInt(parts[0], 10);
                    const monthName = parts[1];
                    
                    // Convert month name to month index (0-11)
                    const monthNames = ["january", "february", "march", "april", "may", "june", 
                                       "july", "august", "september", "october", "november", "december"];
                    const month = monthNames.indexOf(monthName.toLowerCase());
                    
                    if (month !== -1) {
                        // If year is specified, use it; otherwise determine the correct year
                        const year = parts.length > 2 && /^\d{4}$/.test(parts[2]) ? 
                            parseInt(parts[2], 10) : 
                            determineCorrectYear(month, day);
                            
                        return new Date(year, month, day);
                    }
                } else {
                    // It's "June 15, 2023" format
                    const monthDayMatch = dateStr.match(/([A-Za-z]+)\s+(\d{1,2})/i);
                    if (monthDayMatch) {
                        const monthName = monthDayMatch[1];
                        const day = parseInt(monthDayMatch[2], 10);
                        
                        // Convert month name to month index (0-11)
                        const monthNames = ["january", "february", "march", "april", "may", "june", 
                                           "july", "august", "september", "october", "november", "december"];
                        const month = monthNames.indexOf(monthName.toLowerCase());
                        
                        if (month !== -1) {
                            // If year is specified in the string, use it; otherwise determine the correct year
                            const yearMatch = dateStr.match(/\d{4}$/);
                            const year = yearMatch ? parseInt(yearMatch[0], 10) : determineCorrectYear(month, day);
                            
                            return new Date(year, month, day);
                        }
                    }
                }
                return null;
            }
        },
        {
            // Pattern: "This auction will end on Friday, June 15 at 9 PM" or "Auction will end MAY 14th @ 10:00pm Central time"
            // or "Auction for this item will end the morning of May 10th"
            pattern: /(?:auction(?:\s+for\s+this\s+item)?\s+will\s+end|will\s+end)(?:\s+the\s+(?:morning|afternoon|evening)\s+of|\s+on)?(?:\s+)?([A-Za-z]+,?\s+[A-Za-z]+\s+\d{1,2}(?:st|nd|rd|th)?|[A-Za-z]+\s+\d{1,2}(?:st|nd|rd|th)?)/i,
            extract: (match) => {
                // Format: "Friday, June 15" or "MAY 14th" or similar
                const dateStr = match[1].replace(/(?:st|nd|rd|th)/g, '');
                
                // Extract month and day
                const monthDayMatch = dateStr.match(/([A-Za-z]+)\s+(\d{1,2})/i);
                if (monthDayMatch) {
                    const monthName = monthDayMatch[1];
                    const day = parseInt(monthDayMatch[2], 10);
                    
                    // Convert month name to month index (0-11)
                    const monthNames = ["january", "february", "march", "april", "may", "june", 
                                       "july", "august", "september", "october", "november", "december"];
                    const month = monthNames.indexOf(monthName.toLowerCase());
                    
                    if (month !== -1) {
                        const year = determineCorrectYear(month, day);
                        return new Date(year, month, day);
                    }
                }
                return null;
            }
        },
        {
            // Pattern: "Bidding ends Friday" or "Bidding ends Friday, June 15" or "Bids close Sunday May 11th at Midnight Central Time"
            pattern: /(?:bid(?:s|ding)\s+(?:end(?:s|ing)|clos(?:e|es|ing)))\s+([A-Za-z]+(?:,?\s+[A-Za-z]+\s+\d{1,2}(?:st|nd|rd|th)?)?(?:\s+at\s+[A-Za-z0-9:]+(?:\s+[A-Za-z]+\s+[A-Za-z]+)?)?)/i,
            extract: (match) => {
                const dateStr = match[1].replace(/(?:st|nd|rd|th)/g, '').split(/\s+at\s+/)[0]; // Remove time part
                
                // If it's just a day of week like "Friday"
                if (/^[A-Za-z]+$/.test(dateStr)) {
                    const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
                        .indexOf(dateStr.toLowerCase());
                    
                    if (dayOfWeek !== -1) {
                        const resultDate = new Date(listDate);
                        const currentDay = listDate.getDay();
                        const daysToAdd = (dayOfWeek + 7 - currentDay) % 7;
                        resultDate.setDate(listDate.getDate() + (daysToAdd === 0 ? 7 : daysToAdd));
                        return resultDate;
                    }
                } else {
                    // It's a more complete date like "Friday, June 15" or "Sunday May 11th"
                    const monthDayMatch = dateStr.match(/([A-Za-z]+)\s+([A-Za-z]+)\s+(\d{1,2})/i);
                    if (monthDayMatch) {
                        // Format: "Sunday May 11th"
                        const monthName = monthDayMatch[2];
                        const day = parseInt(monthDayMatch[3], 10);
                        
                        // Convert month name to month index (0-11)
                        const monthNames = ["january", "february", "march", "april", "may", "june", 
                                           "july", "august", "september", "october", "november", "december"];
                        const month = monthNames.indexOf(monthName.toLowerCase());
                        
                        if (month !== -1) {
                            const year = determineCorrectYear(month, day);
                            return new Date(year, month, day);
                        }
                    } else {
                        // Try simpler format like "June 15"
                        const simpleDateMatch = dateStr.match(/([A-Za-z]+)\s+(\d{1,2})/i);
                        if (simpleDateMatch) {
                            const monthName = simpleDateMatch[1];
                            const day = parseInt(simpleDateMatch[2], 10);
                            
                            // Convert month name to month index (0-11)
                            const monthNames = ["january", "february", "march", "april", "may", "june", 
                                               "july", "august", "september", "october", "november", "december"];
                            const month = monthNames.indexOf(monthName.toLowerCase());
                            
                            if (month !== -1) {
                                const year = determineCorrectYear(month, day);
                                return new Date(year, month, day);
                            }
                        }
                    }
                }
                return null;
            }
        },
        {
            // Pattern: "End date: June 15, 2023" or "End Date - 6/15/2023"
            pattern: /(?:end\s+date[:\s-]+)([A-Za-z]+\s+\d{1,2}(?:st|nd|rd|th)?,?\s+\d{4}|\d{1,2}\/\d{1,2}\/\d{2,4})/i,
            extract: (match) => {
                const dateStr = match[1].replace(/(?:st|nd|rd|th)/g, '');
                
                // Check if it's MM/DD/YYYY format
                if (/\d{1,2}\/\d{1,2}\/\d{2,4}/.test(dateStr)) {
                    const dateParts = dateStr.split('/');
                    const month = parseInt(dateParts[0], 10) - 1;
                    const day = parseInt(dateParts[1], 10);
                    let year = parseInt(dateParts[2], 10);
                    if (year < 100) {
                        year += year < 50 ? 2000 : 1900;
                    }
                    return new Date(year, month, day);
                } else {
                    // It's "June 15, 2023" format
                    const monthDayMatch = dateStr.match(/([A-Za-z]+)\s+(\d{1,2})/i);
                    if (monthDayMatch) {
                        const monthName = monthDayMatch[1];
                        const day = parseInt(monthDayMatch[2], 10);
                        
                        // Convert month name to month index (0-11)
                        const monthNames = ["january", "february", "march", "april", "may", "june", 
                                           "july", "august", "september", "october", "november", "december"];
                        const month = monthNames.indexOf(monthName.toLowerCase());
                        
                        if (month !== -1) {
                            // If year is specified in the string, use it; otherwise determine the correct year
                            const yearMatch = dateStr.match(/\d{4}$/);
                            const year = yearMatch ? parseInt(yearMatch[0], 10) : determineCorrectYear(month, day);
                            
                            return new Date(year, month, day);
                        }
                    }
                    return null;
                }
            }
        },
        {
            // Pattern: "Bids will stay open until 11:59 Sunday night of May 18th."
            pattern: /(?:bids|auction)\s+will\s+(?:stay\s+open|remain\s+open)\s+until\s+(?:\d{1,2}(?::\d{2})?(?:am|pm|AM|PM)?\s+)?(?:[A-Za-z]+\s+(?:night|morning|afternoon|evening)\s+of\s+)?([A-Za-z]+\s+\d{1,2}(?:st|nd|rd|th)?)/i,
            extract: (match) => {
                const dateStr = match[1].replace(/(?:st|nd|rd|th)/g, '');
                
                // Extract month and day
                const monthDayMatch = dateStr.match(/([A-Za-z]+)\s+(\d{1,2})/i);
                if (monthDayMatch) {
                    const monthName = monthDayMatch[1];
                    const day = parseInt(monthDayMatch[2], 10);
                    
                    // Convert month name to month index (0-11)
                    const monthNames = ["january", "february", "march", "april", "may", "june", 
                                       "july", "august", "september", "october", "november", "december"];
                    const month = monthNames.indexOf(monthName.toLowerCase());
                    
                    if (month !== -1) {
                        const year = determineCorrectYear(month, day);
                        return new Date(year, month, day);
                    }
                }
                return null;
            }
        },
        {
            // Pattern: "Don't forget that end of day on May 14 is the VFM auction deadline."
            pattern: /(?:end\s+of\s+day|deadline)\s+(?:on|is|for)\s+([A-Za-z]+\s+\d{1,2}(?:st|nd|rd|th)?)/i,
            extract: (match) => {
                const dateStr = match[1].replace(/(?:st|nd|rd|th)/g, '');
                
                // Extract month and day
                const monthDayMatch = dateStr.match(/([A-Za-z]+)\s+(\d{1,2})/i);
                if (monthDayMatch) {
                    const monthName = monthDayMatch[1];
                    const day = parseInt(monthDayMatch[2], 10);
                    
                    // Convert month name to month index (0-11)
                    const monthNames = ["january", "february", "march", "april", "may", "june", 
                                       "july", "august", "september", "october", "november", "december"];
                    const month = monthNames.indexOf(monthName.toLowerCase());
                    
                    if (month !== -1) {
                        const year = determineCorrectYear(month, day);
                        return new Date(year, month, day);
                    }
                }
                return null;
            }
        },
        {
            // Pattern: "AUCTION (ends 5/11 at 6pm CST)"
            pattern: /\((?:ends|ending|closing)\s+(\d{1,2}\/\d{1,2}(?:\/\d{2,4})?(?:\s+at\s+\d{1,2}(?::\d{2})?(?:am|pm|AM|PM)?(?:\s+[A-Za-z]{3,4})?)?)\)/i,
            extract: (match) => {
                // Format: "5/11 at 6pm CST"
                // Extract just the date part
                const dateStr = match[1].split(/\s+at\s+/)[0];
                const dateParts = dateStr.split('/');
                
                if (dateParts.length === 3) {
                    const month = parseInt(dateParts[0], 10) - 1; // JS months are 0-indexed
                    const day = parseInt(dateParts[1], 10);
                    let year = parseInt(dateParts[2], 10);
                    // Handle 2-digit years
                    if (year < 100) {
                        year += year < 50 ? 2000 : 1900;
                    }
                    return new Date(year, month, day);
                } else if (dateParts.length === 2) {
                    // Handle MM/DD format without year
                    const month = parseInt(dateParts[0], 10) - 1;
                    const day = parseInt(dateParts[1], 10);
                    const year = determineCorrectYear(month, day);
                    return new Date(year, month, day);
                }
                return null;
            }
        }
    ];

    // Try each pattern
    for (const { pattern, extract } of datePatterns) {
        const match = inputString.match(pattern);
        if (match && match[1]) {
            try {
                const extractedDate = extract(match);
                
                // Validate the date is reasonable
                if (extractedDate && !isNaN(extractedDate.getTime())) {
                    // Make sure the end date is not more than 3 months before the listing date
                    // or more than 1 year after the listing date
                    const threeMonthsBeforeListingDate = new Date(listDate);
                    threeMonthsBeforeListingDate.setMonth(listDate.getMonth() - 3);
                    
                    const oneYearAfterListingDate = new Date(listDate);
                    oneYearAfterListingDate.setFullYear(listDate.getFullYear() + 1);
                    
                    if (extractedDate >= threeMonthsBeforeListingDate && 
                        extractedDate <= oneYearAfterListingDate) {
                        return extractedDate;
                    }
                }
            } catch (e) {
                console.error("Error extracting date:", e);
            }
        }
    }

    return null;
}

export const findPriceAndType = (inputString) => {

    // Remove crossed-out text
    inputString = inputString.replace(/\[-\][^]*?\[\/-\]/g, '');
    inputString = inputString.replace(/\[[^[\]]*?\]/g, '');
    
    // Build regex pattern from PRICE_TYPES enum values
    const priceTypeValues = [
        PRICE_TYPES.STARTING_BID,
        PRICE_TYPES.BUY_IT_NOW,
        PRICE_TYPES.FIXED_PRICE,
        PRICE_TYPES.STARTING_BID_FULL,
        PRICE_TYPES.SOFT_RESERVE
    ];
    const priceTypeRegex = priceTypeValues.join('|');

    // Regex for structured prices
    const structuredRegex = new RegExp(`(?:${priceTypeRegex}):?.*?(?<=^|[ \\t$£€])\\$?(\\d+(?:\\.\\d{1,2})?)`, 'g');
    // Regex for currency accompanied prices
    const currencyRegex = /[$£€](\d+(?:\.\d{1,2})?)/g;
    // Regex for unstructured prices, excluding adjacent text
    const unstructuredRegex = /(?<=^|[ \t$£€])\$?\d+(?:\.\d{1,2})?(?![a-zA-Z])/g;

    const prices = [];
    let match;

    // Extract structured prices
    while ((match = structuredRegex.exec(inputString)) !== null) {
        const validPrice = parseFloat(match[1]);
        if (validPrice < 1000) { // Ignore prices greater than 999
            prices.push({
                price: validPrice,
                type: match[0].match(new RegExp(`(?:${priceTypeRegex})`, 'g'))[0] || PRICE_TYPES.NOT_FOUND,
            });
        }
    }

    // Fallback to just a currency price if no structured prices
    if (prices.length === 0) {
        while ((match = currencyRegex.exec(inputString)) !== null) {
            const validPrice = parseFloat(match[1]);
            if (validPrice < 1000) { // Ignore prices greater than 999
                prices.push({ 
                    price: validPrice, 
                    type: PRICE_TYPES.UNSTRUCTURED });
            }
        }
    }

    // Fallback to unstructured matches if no structured prices or currency price
    if (prices.length === 0) {
        while ((match = unstructuredRegex.exec(inputString)) !== null) {
            const detectedNumber = match[0].replace(/[^\d.]/g, '');
            const validPrice = parseFloat(detectedNumber);
            if (validPrice < 1000) { // Ignore numbers larger than 999
                prices.push({ price: validPrice, type: PRICE_TYPES.UNSTRUCTURED });
            }
        }
    }

    // If no prices found
    if (prices.length === 0) {
        return {
            price: '?',
            type: PRICE_TYPES.NOT_APPLICABLE,
        };
    }

    // Prioritize by type
    return prices.sort((a, b) => {
        const priority = {
            [PRICE_TYPES.STARTING_BID]: 1,
            [PRICE_TYPES.STARTING_BID_FULL]: 2,
            [PRICE_TYPES.BUY_IT_NOW]: 3,
            [PRICE_TYPES.FIXED_PRICE]: 4,
            [PRICE_TYPES.UNSTRUCTURED]: 5
        };
        return (priority[a.type] || 99) - (priority[b.type] || 99);
    })[0]; // Return the top candidate
}

export const findRemove = (inputString) => {
    const pattern = /\+remove\+|\+withdrawn\+|\+removed\+/i;
    const pattern2 = /^(?:remove|removed|withdrawn)$/i;
    const pattern1result = pattern.test(inputString);
    const pattern2result = pattern2.test(inputString);

    return pattern1result || pattern2result;
}

export const findSold = (inputString) => {
    const pattern = /\bsold\b|\btraded\b(?!\s+notsold\b)/i;
    return pattern.test(inputString);
}

export const extractExtras = (inputString) => {
    const thingIds = [];
    const thingPattern = /\[thing=(\d+)\]/g;
    let match;

    while ((match = thingPattern.exec(inputString)) !== null) {
        thingIds.push(parseInt(match[1], 10));
    }

    return thingIds;
};
